import { motion } from "framer-motion";
import { MessageCircle, Clock, CheckCircle, ArrowRight, Users } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Please enter a valid email").max(255, "Email must be less than 255 characters"),
  company: z.string().trim().min(1, "Company is required").max(100, "Company must be less than 100 characters"),
  role: z.string().trim().max(100, "Role must be less than 100 characters").optional(),
  challenge: z.string().min(1, "Please select a challenge"),
  message: z.string().trim().max(2000, "Message must be less than 2000 characters").optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

const Contact = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  
  const challenges = [
    { value: "pipeline", label: t("contact.challenge.pipeline") },
    { value: "measurement", label: t("contact.challenge.measurement") },
    { value: "strategy", label: t("contact.challenge.strategy") },
    { value: "efficiency", label: t("contact.challenge.efficiency") },
    { value: "team", label: t("contact.challenge.team") },
    { value: "other", label: t("contact.challenge.other") },
  ];

  const expectations = [
    { icon: Clock, text: t("contact.expect.1") },
    { icon: MessageCircle, text: t("contact.expect.2") },
    { icon: ArrowRight, text: t("contact.expect.3") },
    { icon: CheckCircle, text: t("contact.expect.4") },
  ];

  const contactFaqs = [
    {
      question: t("contact.faq.q1"),
      answer: t("contact.faq.a1"),
    },
    {
      question: t("contact.faq.q2"),
      answer: t("contact.faq.a2"),
    },
    {
      question: t("contact.faq.q3"),
      answer: t("contact.faq.a3"),
    },
    {
      question: t("contact.faq.q4"),
      answer: t("contact.faq.a4"),
    },
  ];
  
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      role: "",
      challenge: "",
      message: "",
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    try {
      const { error } = await supabase.functions.invoke("contact-form", {
        body: data,
      });

      if (error) throw error;

      toast({
        title: t("contact.toast.title"),
        description: t("contact.toast.description"),
      });
      reset();
    } catch (err) {
      console.error("Form submission error:", err);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 lg:py-24 relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-primary/5 rounded-full blur-[100px]" />
          </div>

          <div className="section-container relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
              {/* Left Column - Form */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-8">
                  <MessageCircle className="w-8 h-8 text-primary" />
                </div>

                <h1 className="font-display text-4xl sm:text-5xl font-bold text-foreground mb-4">
                  {t("contact.title")}
                </h1>

                <p className="text-lg text-muted-foreground mb-8">
                  {t("contact.subtitle")}
                </p>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">
                        {t("contact.form.name")} <span className="text-destructive">{t("contact.form.required")}</span>
                      </Label>
                      <Input
                        id="name"
                        placeholder={t("contact.form.namePlaceholder")}
                        {...register("name")}
                        className={errors.name ? "border-destructive" : ""}
                      />
                      {errors.name && (
                        <p className="text-sm text-destructive">
                          {errors.name.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">
                        {t("contact.form.email")} <span className="text-destructive">{t("contact.form.required")}</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder={t("contact.form.emailPlaceholder")}
                        {...register("email")}
                        className={errors.email ? "border-destructive" : ""}
                      />
                      {errors.email && (
                        <p className="text-sm text-destructive">
                          {errors.email.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company">
                        {t("contact.form.company")} <span className="text-destructive">{t("contact.form.required")}</span>
                      </Label>
                      <Input
                        id="company"
                        placeholder={t("contact.form.companyPlaceholder")}
                        {...register("company")}
                        className={errors.company ? "border-destructive" : ""}
                      />
                      {errors.company && (
                        <p className="text-sm text-destructive">
                          {errors.company.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="role">{t("contact.form.role")}</Label>
                      <Input
                        id="role"
                        placeholder={t("contact.form.rolePlaceholder")}
                        {...register("role")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="challenge">
                      {t("contact.form.challenge")} <span className="text-destructive">{t("contact.form.required")}</span>
                    </Label>
                    <Select
                      onValueChange={(value) => setValue("challenge", value)}
                    >
                      <SelectTrigger
                        className={errors.challenge ? "border-destructive" : ""}
                      >
                        <SelectValue placeholder={t("contact.form.challengePlaceholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        {challenges.map((challenge) => (
                          <SelectItem
                            key={challenge.value}
                            value={challenge.value}
                          >
                            {challenge.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.challenge && (
                      <p className="text-sm text-destructive">
                        {errors.challenge.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">{t("contact.form.message")}</Label>
                    <Textarea
                      id="message"
                      placeholder={t("contact.form.messagePlaceholder")}
                      rows={4}
                      {...register("message")}
                      className={errors.message ? "border-destructive" : ""}
                    />
                    {errors.message && (
                      <p className="text-sm text-destructive">
                        {errors.message.message}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    variant="cta"
                    size="lg"
                    className="w-full sm:w-auto"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? t("contact.form.submitting") : t("contact.form.submit")}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </motion.div>

              {/* Right Column - What to Expect */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="lg:pt-32"
              >
                <div className="bg-card border border-border/50 rounded-2xl p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <h2 className="font-display text-xl font-semibold text-foreground">
                      {t("contact.expect.title")}
                    </h2>
                  </div>

                  <ul className="space-y-4">
                    {expectations.map((item, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <item.icon className="w-4 h-4 text-primary" />
                        </div>
                        <span className="text-muted-foreground">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Contact FAQ Section */}
        <section className="py-16 lg:py-24 bg-muted/30">
          <div className="section-container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-8 text-center">
                {t("contact.faq.title")}
              </h2>

              <Accordion type="single" collapsible className="space-y-4">
                {contactFaqs.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`item-${index}`}
                    className="bg-card border border-border/50 rounded-xl px-6 data-[state=open]:bg-card/80"
                  >
                    <AccordionTrigger className="text-left text-foreground hover:no-underline py-5 text-base font-medium">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base pb-5">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
